/* eslint-disable camelcase */
import { ProjectProposalInfo } from "@/schemas/project-proposal/project-proposal-info";
import { ProjectDetails, ProjectFileType } from "@/types/projectProposal";

export const safeguardsFieldMapByKey: Record<string, { label: string; fileInputName: string; uploadKey: string }> = {
	NO_POVERTY: {
		label: "1. No Poverty",
		fileInputName: "NO_POVERTY_FILE",
		uploadKey: "sdgPovertyFile",
	},
	ZERO_HUNGER: {
		label: "2. Zero Hunger",
		fileInputName: "ZERO_HUNGER_FILE",
		uploadKey: "sdgZeroHungerFIle",
	},
	GOOD_HEALTH_AND_WELL_BEING: {
		label: "3. Good Health and Well-being",
		fileInputName: "GOOD_HEALTH_AND_WELL_BEING_FILE",
		uploadKey: "sdgGoodHealthFile",
	},
	QUALITY_EDUCATION: {
		label: "4. Quality Education",
		fileInputName: "QUALITY_EDUCATION_FILE",
		uploadKey: "sdgQualityEducationFile",
	},
	GENDER_EQUALITY: {
		label: "5. Gender Equality",
		fileInputName: "GENDER_EQUALITY_FILE",
		uploadKey: "sdgGenderEqualityFile",
	},
	CLEAN_WATER_AND_SANITATION: {
		label: "6. Clean Water and Sanitation",
		fileInputName: "CLEAN_WATER_AND_SANITATION_FILE",
		uploadKey: "sdgCleanWaterFile",
	},
	AFFORDABLE_AND_CLEAN_ENERGY: {
		label: "7. Affordable and Clean Energy",
		fileInputName: "AFFORDABLE_AND_CLEAN_ENERGY_FILE",
		uploadKey: "sdgAffordableEnergyFile",
	},
	DECENT_WORK_AND_ECONOMIC_GROWTH: {
		label: "8. Decent Work and Economic Growth",
		fileInputName: "DECENT_WORK_AND_ECONOMIC_GROWTH_FILE",
		uploadKey: "sdgDecentWorkFile",
	},
	INDUSTRY_INNOVATION_AND_INFRASTRUCTURE: {
		label: "9. Industry, Innovation and Infrastructure",
		fileInputName: "INDUSTRY_INNOVATION_AND_INFRASTRUCTURE_FILE",
		uploadKey: "sdgIndustrialInnovationFile",
	},
	REDUCED_INEQUALITIES: {
		label: "10. Reduced Inequalities",
		fileInputName: "REDUCED_INEQUALITIES_FILE",
		uploadKey: "sdgReducedInequalityFile",
	},
	SUSTAINABLE_CITIES_AND_COMMUNITIES: {
		label: "11. Sustainable Cities and Communities",
		fileInputName: "SUSTAINABLE_CITIES_AND_COMMUNITIES_FILE",
		uploadKey: "sdgSustainableCitiesFile",
	},
	RESPONSIBLE_CONSUMPTION_AND_PRODUCTION: {
		label: "12. Responsible Consumption and Production",
		fileInputName: "RESPONSIBLE_CONSUMPTION_AND_PRODUCTION_FILE",
		uploadKey: "sdgResponsibleConsumptionFile",
	},
	CLIMATE_ACTION: {
		label: "13. Climate Action",
		fileInputName: "CLIMATE_ACTION_FILE",
		uploadKey: "sdgClimateActionFile",
	},
	LIFE_BELOW_WATER: {
		label: "14. Life Below Water",
		fileInputName: "LIFE_BELOW_WATER_FILE",
		uploadKey: "sdgLifeBelowWaterFile",
	},
	LIFE_ON_LAND: {
		label: "15. Life on Land",
		fileInputName: "LIFE_ON_LAND_FILE",
		uploadKey: "sdgLifeOnLandFile",
	},
	PEACE_JUSTICE_AND_STRONG_INSTITUTIONS: {
		label: "16. Peace, Justice and Strong Institutions",
		fileInputName: "PEACE_JUSTICE_AND_STRONG_INSTITUTIONS_FILE",
		uploadKey: "sdgPeaceJusticeFile",
	},
	PARTNERSHIPS_FOR_THE_GOALS: {
		label: "17. Partnerships for the Goals",
		fileInputName: "PARTNERSHIPS_FOR_THE_GOALS_FILE",
		uploadKey: "sdgPartnershipsFile",
	},
};

export function getInitialvaluesForSDG(projectDetails?: ProjectDetails) {
	const initial = {
		NO_POVERTY: undefined,
		NO_POVERTY_FILE: undefined,
		ZERO_HUNGER: undefined,
		ZERO_HUNGER_FILE: undefined,
		GOOD_HEALTH_AND_WELL_BEING: undefined,
		GOOD_HEALTH_AND_WELL_BEING_FILE: undefined,
		QUALITY_EDUCATION: undefined,
		QUALITY_EDUCATION_FILE: undefined,
		GENDER_EQUALITY: undefined,
		GENDER_EQUALITY_FILE: undefined,
		CLEAN_WATER_AND_SANITATION: undefined,
		CLEAN_WATER_AND_SANITATION_FILE: undefined,
		AFFORDABLE_AND_CLEAN_ENERGY: undefined,
		AFFORDABLE_AND_CLEAN_ENERGY_FILE: undefined,
		DECENT_WORK_AND_ECONOMIC_GROWTH: undefined,
		DECENT_WORK_AND_ECONOMIC_GROWTH_FILE: undefined,
		INDUSTRY_INNOVATION_AND_INFRASTRUCTURE: undefined,
		INDUSTRY_INNOVATION_AND_INFRASTRUCTURE_FILE: undefined,
		REDUCED_INEQUALITIES: undefined,
		SDG_SUSTAINABLE_CITIES_AND_COMMUNITIES: undefined,
		SDG_SUSTAINABLE_CITIES_AND_COMMUNITIES_FILE: undefined,
		REDUCED_INEQUALITIES_FILE: undefined,
		RESPONSIBLE_CONSUMPTION_AND_PRODUCTION: undefined,
		RESPONSIBLE_CONSUMPTION_AND_PRODUCTION_FILE: undefined,
		CLIMATE_ACTION: undefined,
		CLIMATE_ACTION_FILE: undefined,
		LIFE_BELOW_WATER: undefined,
		LIFE_BELOW_WATER_FILE: undefined,
		LIFE_ON_LAND: undefined,
		LIFE_ON_LAND_FILE: undefined,
		PEACE_JUSTICE_AND_STRONG_INSTITUTIONS: undefined,
		PEACE_JUSTICE_AND_STRONG_INSTITUTIONS_FILE: undefined,
		PARTNERSHIPS_FOR_THE_GOALS: undefined,
		PARTNERSHIPS_FOR_THE_GOALS_FILE: undefined,
	};

	if (!projectDetails) {
		return initial;
	}

	const sortedSdgList = projectDetails?.sdgList?.sort((a, b) => a.sequence - b.sequence) ?? [];

	for (let i = 0; i < sortedSdgList.length; i++) {
		const currentSdg = sortedSdgList[i];
		// @ts-ignore
		initial[`${sortedSdgList[i].key}`] = currentSdg.description;
		// @ts-ignore
		initial[`${sortedSdgList[i].key}_FILE`] = currentSdg.fileUrl
			? {
					fileId: currentSdg.fileId,
					fileUrl: currentSdg.fileUrl,
					fileName: currentSdg.fileName,
					fileSize: currentSdg.fileSize,
					fileType: currentSdg.fileType,
			  }
			: undefined;
	}

	return initial;
}

export function getInitialValuesForSafeguardsFields(projectDetails?: ProjectDetails) {
	const initial = {
		environmental_risk: projectDetails?.environmentalRisks || "",
		environmental_benefits: projectDetails?.environmentalBenefits || "",
		biodiversity_threads: projectDetails?.bioDiversityThreats || "",
		biodiversity_benifits: projectDetails?.bioDiversityBenefits || "",
		pollution_preventaion: projectDetails?.pollutionPrevention || "",
		social_risk: projectDetails?.socialRisks || "",
		social_benefit: projectDetails?.socialBenefits || "",
		community_benefits: projectDetails?.communityBenefits || "",
		jobs: projectDetails?.jobs || "",
		labour_rights: projectDetails?.laborRights || "",
		fair_treatment: projectDetails?.fairTreatment || "",
		child_labour: projectDetails?.noChildLabor || "",
		contract_workers: projectDetails?.contractWorkersProtection || "",
		indigenous_local_knowledge: projectDetails?.indigenousLocalKnowledgeUtilization || "",
		community: projectDetails?.communityConsideration || "",
		gender_equity: projectDetails?.genderEquity || "",
		lands_terrories: projectDetails?.noNegativeImpactInLandAndTerritories || "",
		cultural_heritage: projectDetails?.culturalHeritage || "",
		public_health: projectDetails?.publicHealth || "",
		redd_plus_cancun: projectDetails?.reddPlusCancun || "",
		water_use_and_efficiency: projectDetails?.waterUseAndEfficiency || "",
		soil_and_land_protection: projectDetails?.soilAndLandProtection || "",
		habitat_and_ecosystem_protection: projectDetails?.habitatAndEcosystemProtection || "",
	};

	return initial;
}

export function getInitialValuesForSafeguardsFiles(projectDetails?: ProjectDetails) {
	const initial = {
		environmental_risk_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_ENVIRONMENTAL_RISKS,
			) || undefined,
		environmental_benefits_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_ENVIRONMENTAL_BENEFITS,
			) || undefined,
		biodiversity_benifits_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_BIO_DIVERSITY_BENEFITS,
			) || undefined,
		biodiversity_threads_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_BIO_DIVERSITY_THREATS,
			) || undefined,
		pollution_preventaion_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_POLLUTION_PREVENTION,
			) || undefined,
		social_risk_file:
			projectDetails?.fileList?.find((file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_SOCIAL_RISKS) ||
			undefined,
		social_benefit_file:
			projectDetails?.fileList?.find((file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_SOCIAL_BENEFITS) ||
			undefined,
		community_benefits_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_COMMUNITY_BENEFITS,
			) || undefined,
		jobs_file:
			projectDetails?.fileList?.find((file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_JOBS) || undefined,
		labour_rights_file:
			projectDetails?.fileList?.find((file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_LABOR_RIGHTS) ||
			undefined,
		fair_treatment_file:
			projectDetails?.fileList?.find((file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_FAIR_TREATMENT) ||
			undefined,
		child_labour_file:
			projectDetails?.fileList?.find((file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_NO_CHILD_LABOR) ||
			undefined,
		contract_workers_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_CONTRACT_WORKERS_PROTECTION,
			) || undefined,
		indigenous_local_knowledge_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_INDIGENOUS_LOCAL_KNOWLEDGE_UTILIZATION,
			) || undefined,
		community_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_COMMUNITY_CONSIDERATION,
			) || undefined,
		gender_equity_file:
			projectDetails?.fileList?.find((file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_GENDER_EQUITY) ||
			undefined,
		lands_terrories_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_NO_NEGATIVE_IMPACT_IN_LAND_AND_TERRITORIES,
			) || undefined,
		cultural_heritage_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_CULTURAL_HERITAGE,
			) || undefined,
		public_health_file:
			projectDetails?.fileList?.find((file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_PUBLIC_HEALTH) ||
			undefined,
		redd_plus_cancun_file:
			projectDetails?.fileList?.find((file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_REDD_PLUS_CANCUN) ||
			undefined,
		water_use_and_efficiency_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_WATER_USE_AND_EFFICIENCY,
			) || undefined,
		soil_and_land_protection_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_SOIL_AND_LAND_PROTECTION,
			) || undefined,
		habitat_and_ecosystem_protection_file:
			projectDetails?.fileList?.find(
				(file) => file.fileType === ProjectFileType.PROJECT_SAFEGUARDS_HABITAT_AND_ECOSYSTEM_PROTECTION,
			) || undefined,
	};

	return initial;
}

export function getInitialValuesForPersistentFileList(projectDetails?: ProjectDetails) {
	if (!projectDetails) {
		return [];
	}

	const existingFileList = (projectDetails.fileList || []).filter((e) => {
		return e && e.fileType && ProjectFileType[e.fileType] != undefined;
	});

	const safeGuardsFileItemMappedFromSdgList =
		projectDetails?.sdgList
			?.filter((sdgItem) => {
				return !!sdgItem.fileUrl;
			})
			?.map((sdgItem) => {
				if (!sdgItem) return;
				return {
					fileId: sdgItem.fileId,
					fileUrl: sdgItem.fileUrl,
					fileName: sdgItem.fileName,
					fileSize: sdgItem.fileSize,
					fileType: sdgItem.fileType,
				};
			}) ?? [];
	return [...existingFileList, ...safeGuardsFileItemMappedFromSdgList];
}

export function getPayloadForSafeguardsFiles(data: ProjectProposalInfo) {
	const {
		environmental_risk_file,
		environmental_benefits_file,
		biodiversity_benifits_file,
		biodiversity_threads_file,
		pollution_preventaion_file,
		social_risk_file,
		social_benefit_file,
		community_benefits_file,
		jobs_file,
		labour_rights_file,
		fair_treatment_file,
		child_labour_file,
		contract_workers_file,
		indigenous_local_knowledge_file,
		community_file,
		gender_equity_file,
		lands_terrories_file,
		cultural_heritage_file,
		public_health_file,
		redd_plus_cancun_file,
		water_use_and_efficiency_file,
		soil_and_land_protection_file,
		habitat_and_ecosystem_protection_file,
	} = data;

	const payload = {
		projectSafeguardsEnvironmentalRisksFile: environmental_risk_file,
		projectSafeguardsEnvironmentalBenefitsFile: environmental_benefits_file,
		projectSafeguardsBioDiversityThreatsFile: biodiversity_threads_file,
		projectSafeguardsBioDiversityBenefitsFile: biodiversity_benifits_file,
		projectSafeguardsPollutionPreventionFile: pollution_preventaion_file,
		projectSafeguardsSocialRisksFile: social_risk_file,
		projectSafeguardsSocialBenefitsFile: social_benefit_file,
		projectSafeguardsCommunityBenefitsFile: community_benefits_file,
		projectSafeguardsJobsFile: jobs_file,
		projectSafeguardsLaborRightsFile: labour_rights_file,
		projectSafeguardsFairTreatmentFile: fair_treatment_file,
		projectSafeguardsNoChildLaborFile: child_labour_file,
		projectSafeguardsContractWorkersProtectionFile: contract_workers_file,
		projectSafeguardsIndigenousLocalKnowledgeFile: indigenous_local_knowledge_file,
		projectSafeguardsCommunityConsiderationFile: community_file,
		projectSafeguardsGenderEquityFile: gender_equity_file,
		projectSafeguardsNoNegativeImpactInLandAndTerritoriesFile: lands_terrories_file,
		projectSafeguardsCulturalHeritageFile: cultural_heritage_file,
		projectSafeguardsPublicHealthFile: public_health_file,
		projectSafeguardsHabitatAndEcosystemProtectionFile: habitat_and_ecosystem_protection_file,
		projectSafeguardsSoilAndLandProtectionFile: soil_and_land_protection_file,
		projectSafeguardsWaterUseAndEfficiencyFile: water_use_and_efficiency_file,
		projectSafeguardsReddPlusCancunFile: redd_plus_cancun_file,
	};

	return payload;
}
